import {
  CHART_ALL_FEEDBACK_ALIAS,
  CHART_FIELD,
  CHART_TYPE,
} from '@/entities/chart/model/constants';
import type { ChartExportProps } from '@/features/(charts)/chart-detail/hooks/use-chart-export';
import useChartLabel from '@/features/(charts)/chart-renderer/hooks/use-chart-label';
import chartUtils from '@/features/(charts)/chart-renderer/lib/chart-utils';
import { ChartFieldMap } from '@/features/(charts)/chart-renderer/model/chart-maps';
import { getTimezoneDayjs } from '@/utils/time';

export const useSingleSeriesExport = (
  props: ChartExportProps & { enabled: boolean },
) => {
  const { chartBase, tableData, enabled } = props;
  const getLabel = useChartLabel(tableData?.field_item_meta);
  const tableType = chartUtils.getTableType(
    chartBase.type,
    chartBase.historical.value,
  );

  const getCurrentTable = (): string => {
    if (!tableData) throw new Error('Table data is required');
    const rowItems = chartBase.field.entire
      ? (tableData.tables[0] || []).map((d) => d.x)
      : chartBase.field.candidates || [];
    if (tableType === 'single') {
      // single-axis acc data
      const data = tableData as ChartSingleTableRes;
      const columns = ['Item', 'Value'];
      const rows: [string, number][] = rowItems.map((item) => [
        getLabel(chartBase.field.type, item),
        (data.tables[0] || []).find((x) => x.x === item)?.value || 0,
      ]);
      const sortedRows = rows.sort((a, b) => b[1] - a[1]);
      const csv = [columns, ...sortedRows]
        .map((row) => row.join(','))
        .join('\n');
      return csv;
    } else {
      const data = tableData as ChartCrossTableRes;
      if (chartBase.type === CHART_TYPE.SINGLE_AXIS) {
        // single-axis time-series data
        const frequency = chartBase.historical.frequency;
        if (!frequency) throw new Error('Frequency is required');
        const today = getTimezoneDayjs();
        const dateRange = chartUtils.getTimeRangeByTerm(
          today,
          chartBase.target.term,
        );
        const dateLabels = chartUtils.getDateLabelsByUnit({
          unit: chartBase.target.term.unit,
          dateRange,
        });

        const columns = ['Item', ...dateLabels, 'Total'];

        const formatFn = chartUtils.getFormatByFrequency(frequency);
        const rows = rowItems.map((item) => {
          const targetRow = (data.tables[0] || []).find((x) => x.x === item);
          return [
            getLabel(chartBase.field.type, item),
            ...dateLabels.map((date) => {
              if (!targetRow) return dateLabels.map(() => 0);
              return (
                targetRow.values.find(
                  (y) =>
                    formatFn(getTimezoneDayjs(y.y.toString())) === date,
                )?.value || 0
              );
            }),
            targetRow?.values.reduce((acc, curr) => acc + curr.value, 0) || 0,
          ];
        });

        const sortedRows = rows.sort((a, b) => {
          const totalA = a[a.length - 1];
          const totalB = b[b.length - 1];
          if (typeof totalA === 'number' && typeof totalB === 'number') {
            return totalB - totalA;
          }
          return 0;
        });

        const csv = [columns, ...sortedRows]
          .map((row) => row.join(','))
          .join('\n');
        return csv;
      }
      if (chartBase.type === CHART_TYPE.CROSS_AXIS) {
        // cross-axis data with multiple chart series
        const allCrossCandidates: string[] = [];
        const allCrossFields: ChartFieldDef[] = [];

        // Collect all cross candidates from all chart series
        chartBase.series.forEach((series) => {
          if (series.cross_field) {
            const candidates =
              series.cross_field.type === CHART_FIELD.FEEDBACK_ALL
                ? [CHART_ALL_FEEDBACK_ALIAS]
                : series.cross_field.candidates || [];
            candidates.forEach((candidate) => {
              if (!allCrossCandidates.includes(candidate.toString())) {
                allCrossCandidates.push(candidate.toString());
                allCrossFields.push(series.cross_field!);
              }
            });
          }
        });

        if (allCrossCandidates.length === 0)
          throw new Error('Cross field candidates are required');

        const primaryTable = data.tables[0] || [];

        const rows = rowItems.map((item) => {
          const targetRow = primaryTable.find((x) => x.x === item);
          return [
            getLabel(chartBase.field.type, item),
            ...allCrossCandidates.map((y) => {
              if (!targetRow) return 0;
              if (y === CHART_ALL_FEEDBACK_ALIAS) {
                return (
                  targetRow.values.reduce((acc, curr) => acc + curr.value, 0) ||
                  0
                );
              }
              const value = targetRow.values.find(
                (yData) => yData.y.toString() === y.toString(),
              )?.value;
              return value || 0;
            }),
            targetRow?.values.reduce((acc, curr) => acc + curr.value, 0) || 0,
          ];
        });

        const parsedColumns = [
          'Item',
          ...allCrossCandidates.map((y, index) => {
            const correspondingField =
              allCrossFields[index] || allCrossFields[0];
            return getLabel(correspondingField.type, y);
          }),
          'Total',
        ];

        const sortedRows = rows.sort((a, b) => {
          const totalA = a[a.length - 1];
          const totalB = b[b.length - 1];
          if (typeof totalA === 'number' && typeof totalB === 'number') {
            return totalB - totalA;
          }
          return 0;
        });

        const csv = [parsedColumns, ...sortedRows]
          .map((row) => row.join(','))
          .join('\n');
        return csv;
      }
      throw new Error('Invalid table type');
    }
  };

  const getCompareTable = (): string => {
    if (!tableData) throw new Error('Table data is required');
    const compareTable = tableData.compare_tables?.[0];
    if (!compareTable) throw new Error('Compare table is required');

    const rowItems = chartBase.field.entire
      ? compareTable.map((d) => d.x)
      : chartBase.field.candidates || [];

    if (tableType === 'single') {
      // single-axis acc data
      const data = compareTable as ChartSingleTableData;
      const columns = ['Item', 'Value'];
      const rows: [string, number][] = rowItems.map((item) => [
        getLabel(chartBase.field.type, item),
        data.find((x) => x.x === item)?.value || 0,
      ]);
      const sortedRows = rows.sort((a, b) => b[1] - a[1]);
      const csv = [columns, ...sortedRows]
        .map((row) => row.join(','))
        .join('\n');
      return csv;
    } else {
      const data = compareTable as ChartCrossTableData;
      if (chartBase.type === CHART_TYPE.SINGLE_AXIS) {
        // single-axis time-series data
        const frequency = chartBase.historical.frequency;
        if (!frequency) throw new Error('Frequency is required');
        const today = getTimezoneDayjs();
        const dateRange = chartUtils.getCompareTimeRangeByTerm(
          today,
          chartBase.target.term,
          chartBase.target.compare_term,
        );
        const dateLabels = chartUtils.getDateLabelsByUnit({
          unit: chartBase.target.term.unit,
          dateRange,
        });

        const columns = ['Item', ...dateLabels, 'Total'];

        const formatFn = chartUtils.getFormatByFrequency(frequency);
        const rows = rowItems.map((item) => {
          const targetRow = data.find((x) => x.x === item);
          return [
            getLabel(chartBase.field.type, item),
            ...dateLabels.map((date) => {
              if (!targetRow) return 0;
              return (
                targetRow.values.find(
                  (y) =>
                    formatFn(getTimezoneDayjs(y.y.toString())) === date,
                )?.value || 0
              );
            }),
            targetRow?.values.reduce((acc, curr) => acc + curr.value, 0) || 0,
          ];
        });

        const sortedRows = rows.sort((a, b) => {
          const totalA = a[a.length - 1];
          const totalB = b[b.length - 1];
          if (typeof totalA === 'number' && typeof totalB === 'number') {
            return totalB - totalA;
          }
          return 0;
        });

        const csv = [columns, ...sortedRows]
          .map((row) => row.join(','))
          .join('\n');
        return csv;
      }
      if (chartBase.type === CHART_TYPE.CROSS_AXIS) {
        // cross-axis data with multiple chart series
        const allCrossCandidates: string[] = [];
        const allCrossFields: ChartFieldDef[] = [];

        // Collect all cross candidates from all chart series
        chartBase.series.forEach((series) => {
          if (series.cross_field) {
            const candidates =
              series.cross_field.type === CHART_FIELD.FEEDBACK_ALL
                ? [CHART_ALL_FEEDBACK_ALIAS]
                : series.cross_field.candidates || [];

            candidates.forEach((candidate) => {
              if (!allCrossCandidates.includes(candidate.toString())) {
                allCrossCandidates.push(candidate.toString());
                allCrossFields.push(series.cross_field!);
              }
            });
          }
        });

        if (allCrossCandidates.length === 0)
          throw new Error('Cross field candidates are required');

        const rows = rowItems.map((item) => {
          const targetRow = data.find((x) => x.x === item);
          return [
            getLabel(chartBase.field.type, item),
            ...allCrossCandidates.map((y) => {
              if (!targetRow) return 0;
              if (y === CHART_ALL_FEEDBACK_ALIAS) {
                return (
                  targetRow.values.reduce((acc, curr) => acc + curr.value, 0) ||
                  0
                );
              }
              const value = targetRow.values.find(
                (yData) => yData.y.toString() === y.toString(),
              )?.value;
              return value || 0;
            }),
            targetRow?.values.reduce((acc, curr) => acc + curr.value, 0) || 0,
          ];
        });

        const parsedColumns = [
          'Item',
          ...allCrossCandidates.map((y, index) => {
            const correspondingField =
              allCrossFields[index] || allCrossFields[0];
            return getLabel(correspondingField.type, y);
          }),
          'Total',
        ];

        const sortedRows = rows.sort((a, b) => {
          const totalA = a[a.length - 1];
          const totalB = b[b.length - 1];
          if (typeof totalA === 'number' && typeof totalB === 'number') {
            return totalB - totalA;
          }
          return 0;
        });

        const csv = [parsedColumns, ...sortedRows]
          .map((row) => row.join(','))
          .join('\n');
        return csv;
      }
      throw new Error('Invalid table type');
    }
  };

  const getCsvData = () => {
    const isCompare = chartBase.target.is_compare;
    const currentTable = getCurrentTable();
    if (!isCompare) return currentTable;
    const compareTable = getCompareTable();
    return `Current:\n\n${currentTable}\n\n\nCompare:\n\n${compareTable}`;
  };

  const downloadCSV = (csvString: string, filename: string) => {
    const blob = new Blob(['\uFEFF' + csvString], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const getFileName = () => {
    const field = ChartFieldMap.get(chartBase.field.type);
    const dateRange = chartUtils.getTimeRangeByTerm(
      getTimezoneDayjs(),
      chartBase.target.term,
    );
    const dateLabels = chartUtils.getDateLabelsByUnit({
      unit: chartBase.target.term.unit,
      dateRange,
    });
    const startDate = dateLabels[0];
    const endDate = dateLabels[dateLabels.length - 1];

    if (
      chartBase.series.length > 0 &&
      chartBase.series.some((series) => series.cross_field)
    ) {
      const crossFieldNames = chartBase.series
        .filter((series) => series.cross_field)
        .map((series) => ChartFieldMap.get(series.cross_field!.type))
        .join(' + ');
      return `${field} / ${crossFieldNames} / ${startDate} - ${endDate}`;
    }
    return `${field} / ${startDate} - ${endDate}`;
  };

  const handleSingleSeriesExport = () => {
    if (!enabled) return;
    const csvData = getCsvData();
    const fileName = getFileName();
    downloadCSV(csvData, fileName);
  };

  return handleSingleSeriesExport;
};
